/**
 * Détection du mode d'affichage de l'application.
 *
 * Le manifest déclare `"display": "standalone"` : lorsque le site est lancé depuis l'icône
 * ajoutée à l'écran d'accueil, la barre d'adresse du navigateur disparaît — et avec elle,
 * sur iOS, le seul moyen de recharger la page. C'est ce contexte que l'on identifie ici pour
 * n'activer le pull-to-refresh maison que là où il est réellement nécessaire.
 */

// `fullscreen` et `minimal-ui` sont des variantes d'installation retenues par certains
// navigateurs : elles masquent elles aussi la barre d'adresse, le besoin y est identique.
const STANDALONE_DISPLAY_MODES = ['standalone', 'fullscreen', 'minimal-ui'];

const PWA_ATTRIBUTE = 'pwa';
const PWA_VALUE = 'standalone';

/**
 * Vrai lorsque l'app tourne en PWA installée (écran d'accueil iOS/Android, app desktop).
 *
 * @returns {boolean}
 */
export function isStandalone() {
    if (typeof window === 'undefined') return false;

    // iOS Safari : propriété historique, seule fiable sur les versions antérieures à iOS 16.4.
    if (window.navigator.standalone === true) return true;

    if (typeof window.matchMedia !== 'function') return false;

    return STANDALONE_DISPLAY_MODES.some(
        mode => window.matchMedia(`(display-mode: ${mode})`).matches
    );
}

/**
 * Pose `data-pwa="standalone"` sur `<html>` — même principe que le `data-theme` du theming —
 * afin que le CSS puisse cibler le mode PWA sans dépendre de React. À appeler au boot, avant
 * le render, pour éviter tout flash de comportement.
 */
export function initPwaDisplayMode() {
    if (typeof document === 'undefined') return;

    const apply = () => {
        if (isStandalone()) document.documentElement.dataset[PWA_ATTRIBUTE] = PWA_VALUE;
        else delete document.documentElement.dataset[PWA_ATTRIBUTE];
    };

    apply();

    // Le mode peut changer en cours de session (installation, passage en plein écran).
    const mediaQueryList = window.matchMedia?.('(display-mode: standalone)');
    if (mediaQueryList?.addEventListener) mediaQueryList.addEventListener('change', apply);
}
